<template>
  <section class="call">
    <!-- session header -->
    <div class="sessionBar">
      <div class="sessionMeta">
        <div class="eyebrowRow">
          <span class="eyebrow">Dialer</span>
          <component
            :is="pillTag"
            v-if="statusPill"
            v-bind="pillBind"
            class="statusPill"
            :class="[statusPill.tone, { pillAction: statusPill.action }]"
            @click="onPillClick"
          >
            <span v-if="statusPill.tone === 'amber'" class="pillDot"></span>{{ statusPill.label }}
          </component>
        </div>
      </div>
      <button
        class="btn secondary settingsBtn"
        :aria-pressed="settingsOpen"
        @click="$emit('open-settings')"
      >
        <span>Preferences</span>
        <NotifyDot v-if="prefsDot && prefsDot !== 'none'" overlay :kind="prefsDot" />
      </button>
      <button class="btn secondary" @click="$emit('open-history')">
        <span>History</span>
      </button>
    </div>

    <slot name="body-override">
    <div v-if="entry" class="callBody">
      <!-- WHY: the reason this person, right now -->
      <WhyNowCard :label="entry.label" :why-now="entry.why_now" />

      <!-- Person-level dialability: shown only when EVERY line is blocked —
           entry.dialability is the best of the lines, so a blocked person has
           no dialable line at all. Cooldown needs no notice: each line's own
           status carries it. -->
      <div v-if="isBlocked" class="dialNotice blocked">
        <AppIcon name="about" :size="15" />
        <span>{{ callsUnavailableMessage(entry.country) }}</span>
      </div>

      <!-- WHO: the person, with the one action that names them -->
      <ContactIdentity :entry="entry">
        <template #actions>
          <button
            type="button"
            class="btn primary"
            :disabled="isBlocked"
            :title="isBlocked ? callsUnavailableMessage(entry.country) : 'Tries each of their lines in turn, best first'"
            @click="$emit('call-contact')"
          >
            <AppIcon name="phone_call" :size="15" />{{ callLabel }}
          </button>
        </template>
      </ContactIdentity>

      <!-- CALL SURFACE: their lines, each with its own Call -->
      <PhoneLines :entry="entry" callable @call="(line) => $emit('one-off-call', line)" />

      <!-- BACKGROUND: company + play briefs -->
      <AboutBriefs :entry="entry" :group-color="groupColor" />

      <!-- voicemail Raven left (tied to the last connect) -->
      <VoicemailCard :voicemail="entry.voicemail" />

      <!-- notes -->
      <div class="notesCard surface-card">
        <div class="notesHead">
          <span class="notesLabel"><AppIcon name="pencil" :size="13" />Notes</span>
          <span class="notesHint">Before, during &amp; after the call</span>
        </div>
        <textarea
          v-model="notesDraft"
          class="notesInput"
          placeholder="Jot context, objections, next steps… saved to this contact."
          @blur="onNotesBlur"
        ></textarea>
      </div>
    </div>

    <!-- Quiet until the first load lands: "Queue is clear" is a verdict, not a default. -->
    <div v-else-if="loading" class="loadingPane" aria-busy="true"></div>

    <div v-else class="emptyPane">
      <p class="emptyTitle">Queue is clear</p>
      <template v-if="canAddContacts">
        <p class="emptyText">Add people you recently captured in LeadIQ, or capture more in the extension and they’ll land here.</p>
        <button type="button" class="btn primary emptyAction" @click="$emit('add-contacts')">
          <AppIcon name="plus" :size="15" />Add contacts
        </button>
      </template>
      <p v-else class="emptyText">No contacts are available in your queue right now.</p>
    </div>
    </slot>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import AppIcon from '../../../components/AppIcon.vue'
import NotifyDot from '../NotifyDot.vue'
import ContactIdentity from './ContactIdentity.vue'
import PhoneLines from './PhoneLines.vue'
import WhyNowCard from './WhyNowCard.vue'
import AboutBriefs from './AboutBriefs.vue'
import VoicemailCard from './VoicemailCard.vue'
import type { CallerQueueEntry, QueuedNumber } from '../../../services/dialerApi'
import type { PillDisplay } from '../../composables/useTelephony'
import { callsUnavailableMessage, canOneOffDial } from './dialability'
import type { DotKind } from './settings/phoneSettingsNav'

const props = defineProps<{
  entry: CallerQueueEntry | null
  groupColor: string
  /** The first queue read is still out, so an empty entry says nothing yet. */
  loading?: boolean
  statusPill?: PillDisplay | null
  /** Your own callback number needs verifying — the drawer can fix it. */
  prefsDot?: DotKind
  settingsOpen?: boolean
  /** The rep may bring people into the queue; the empty pane then offers it. */
  canAddContacts?: boolean
}>()

const emit = defineEmits<{
  (e: 'call-contact'): void
  (e: 'add-contacts'): void
  (e: 'one-off-call', line: QueuedNumber): void
  (e: 'save-notes', entryId: string, notes: string): void
  (e: 'open-history'): void
  (e: 'open-settings'): void
}>()

const pillTag = computed(() => {
  if (props.statusPill?.action === 'prefs') return 'button'
  if (props.statusPill?.action === 'phone_settings') return RouterLink
  return 'span'
})
const pillBind = computed(() => {
  if (props.statusPill?.action === 'prefs') return { type: 'button' as const }
  if (props.statusPill?.action === 'phone_settings') return { to: `/settings/phone${props.statusPill.hash ?? ''}` }
  return {}
})
function onPillClick(): void {
  if (props.statusPill?.action === 'prefs') emit('open-settings')
}

// No-local-number contacts are never dialable (the backend /claim also rejects them).
const isBlocked = computed(() =>
  props.entry ? !canOneOffDial(props.entry.dialability) : false,
)

const callLabel = computed(() => {
  const first = String(props.entry?.contact_name || '').trim().split(/\s+/)[0]
  return first ? `Call ${first}` : 'Call contact'
})

// Notes draft — reset when the focused contact changes; persist on blur.
const notesDraft = ref('')
watch(
  () => props.entry?.id,
  () => { notesDraft.value = props.entry?.notes || '' },
  { immediate: true },
)
function onNotesBlur() {
  if (props.entry) emit('save-notes', props.entry.id, notesDraft.value)
}
</script>

<style scoped>
.call { position: relative; display: flex; flex-direction: column; min-width: 0; min-height: 0; overflow: hidden; }
.eyebrow { font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.eyebrowRow { display: flex; align-items: center; gap: var(--space-3); }
.statusPill { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: var(--radius-full); font-size: 11px; font-weight: var(--fw-semibold); white-space: nowrap; }
.statusPill.amber { background: var(--hue-amber-soft); border: 1px solid var(--hue-amber-edge); color: var(--hue-amber); }
.statusPill.success { background: var(--color-success-soft); border: 1px solid var(--color-success-edge); color: var(--color-success); }
.pillDot { width: 7px; height: 7px; border-radius: var(--radius-full); background: currentColor; }
.statusPill.pillAction { cursor: pointer; text-decoration: none; font-family: inherit; }
.statusPill.pillAction:hover { filter: brightness(0.97); }
.statusPill.pillAction:focus-visible { outline: 2px solid var(--color-accent); outline-offset: 2px; }

/* Settings & History reuse the shared .btn secondary pill; Settings is positioned
   so its "setup needed" badge can anchor to the corner. */
.settingsBtn { position: relative; --notify-dot-cutout: var(--color-bg-app); }

.sessionBar { position: relative; z-index: 1; display: flex; align-items: center; gap: var(--space-5); padding: var(--space-5) var(--space-7) var(--space-4); flex-shrink: 0; border-bottom: 1px solid var(--color-border-subtle); }
/* flex:1 is what right-aligns Settings/History now that the ready count (which
   carried margin-left:auto) lives on the rail with the button it describes. */
.sessionMeta { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }

.callBody { flex: 1; min-height: 0; overflow-y: auto; padding: var(--space-5) var(--space-7) var(--space-4); display: flex; flex-direction: column; gap: var(--space-4); }

.dialNotice { display: flex; align-items: flex-start; gap: var(--space-2); padding: var(--space-3) var(--space-4); border-radius: var(--radius-lg); font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-secondary); }
.dialNotice.blocked { background: var(--color-bg-subtle); border: 1px dashed var(--color-border-strong); }

.notesCard { padding: var(--space-4); }
.notesHead { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: var(--space-2); }
.notesLabel { display: inline-flex; align-items: center; gap: 6px; font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.notesHint { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.notesInput { width: 100%; min-height: 84px; resize: vertical; border: 0; background: transparent; font-family: var(--font-sans); font-size: var(--fs-base); line-height: var(--lh-loose); color: var(--color-text-primary); outline: none; }
.notesInput::placeholder { color: var(--color-text-tertiary); }

.loadingPane { flex: 1; min-height: 0; }
/* Overlays the whole card, bar included, so the verdict centres on the page; the
   10vh is SetupCard's optical lift. The bar's z-index keeps its buttons clickable. */
.emptyPane { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-2); text-align: center; color: var(--color-text-tertiary); padding: var(--space-7) var(--space-7) calc(var(--space-7) + 10vh); }
.emptyTitle { font-family: var(--font-display); font-size: var(--fs-lg); color: var(--color-text-secondary); }
.emptyText { font-size: var(--fs-sm); max-width: 360px; }
.emptyAction { margin-top: var(--space-3); }
</style>
